'use client'

import { v4 as uuid } from 'uuid'
import { type TrackV1CreateArgs, TrackingEvent } from 'shared'
import { TablelistProApi } from '@lib/api'
import { logger } from '@lib/logger'

const TRACK_SESSION_ID_STORAGE_KEY = 'tl.track.sessionId'
const TRACK_PAGEVIEW_DEDUPE_PREFIX = 'tl.track.pv'
export const CHECKOUT_REFERRER_WINDOW_MS = 10 * 60 * 1000

type TrackEventArgs = {
  event: TrackingEvent
  venueId: string
  eventId?: string
  inventoryId?: string
  admissionGroupId?: string
  orderId?: string
  pathname?: string
}

const trackApi = new TablelistProApi<void>('/v1/track')

function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') return ''

  const existing = window.sessionStorage.getItem(TRACK_SESSION_ID_STORAGE_KEY)
  if (existing) return existing

  const sessionId = uuid()
  window.sessionStorage.setItem(TRACK_SESSION_ID_STORAGE_KEY, sessionId)
  return sessionId
}

function markPageViewSent(dedupeKey: string): boolean {
  if (typeof window === 'undefined') return false

  const key = `${TRACK_PAGEVIEW_DEDUPE_PREFIX}:${dedupeKey}`
  const existing = window.sessionStorage.getItem(key)
  if (existing) return false

  window.sessionStorage.setItem(key, '1')
  return true
}

function buildPageContext(): TrackV1CreateArgs['context'] | undefined {
  if (typeof window === 'undefined') return undefined

  const page = window.location
  const title =
    typeof document !== 'undefined' ? document.title || undefined : undefined

  return {
    page: {
      pathname: page.pathname,
      search: page.search,
      hash: page.hash,
      referrer: typeof document !== 'undefined' ? document.referrer : undefined,
      title,
      href: page.href,
      protocol: page.protocol,
      host: page.host,
      hostName: page.hostname,
      port: page.port,
    },
    userAgent:
      typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  }
}

function isCheckoutPath(pathname: string): boolean {
  return /^\/(?:w\/)?o\/[^/]+\/?$/.test(pathname)
}

function isStripeCheckoutHost(hostname: string): boolean {
  return hostname === 'checkout.stripe.com' || hostname === 'hooks.stripe.com'
}

export function didArriveFromCheckoutReferrer(): boolean {
  if (typeof document === 'undefined') return false

  const referrer = document.referrer?.trim()
  if (!referrer) return false

  try {
    const referrerUrl = new URL(referrer)

    if (typeof window !== 'undefined') {
      const isSameOriginCheckout =
        referrerUrl.origin === window.location.origin &&
        isCheckoutPath(referrerUrl.pathname)
      if (isSameOriginCheckout) return true
    }

    return isStripeCheckoutHost(referrerUrl.hostname)
  } catch {
    return false
  }
}

export function didArriveFromCheckoutRecently(
  windowMs: number = CHECKOUT_REFERRER_WINDOW_MS
): boolean {
  if (typeof window === 'undefined') return false
  const navigationStartMs = Date.now() - performance.now()
  return (
    didArriveFromCheckoutReferrer() && Date.now() - navigationStartMs < windowMs
  )
}

export async function trackEvent(args: TrackEventArgs): Promise<void> {
  if (typeof window === 'undefined') return

  try {
    const body: TrackV1CreateArgs = {
      event: args.event,
      properties: {
        venueId: args.venueId,
        eventId: args.eventId,
        inventoryId: args.inventoryId,
        admissionGroupId: args.admissionGroupId,
        orderId: args.orderId,
        pathname: args.pathname || window.location.pathname,
      },
      sessionId: getOrCreateSessionId(),
      context: buildPageContext(),
    }
    await trackApi.post<void, TrackV1CreateArgs>(trackApi.basePath, body)
  } catch (error) {
    logger.error('Track event failed', error)
  }
}

export async function trackPageView(options: {
  venueId: string
  eventId?: string
}): Promise<void> {
  if (typeof window === 'undefined') return

  const pathname = window.location.pathname
  const dedupeKey = options.eventId
    ? `event:${options.eventId}:${pathname}`
    : `venue:${options.venueId}:${pathname}`
  if (!markPageViewSent(dedupeKey)) return

  await trackEvent({
    event: TrackingEvent.PageViewed,
    venueId: options.venueId,
    eventId: options.eventId,
    pathname,
  })
}
